import { SnackbarContext } from './useSnackbar'

import React, { FC, ReactNode, useState } from 'react'

import { Box, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import Snackbar from '@mui/material/Snackbar'

import { Alert, GENERIC_ERROR_TEXT, isTechnicalMessage, userFacingMessage } from '@repo/ui'

export interface IAlertProps {
  severity: 'error' | 'success' | 'info' | 'warning' | undefined
  title?: string
  text?: string
  action?: ReactNode
}

interface Props {
  children: ReactNode
}

export const SnackbarProvider: FC<Props> = ({ children }) => {
  const theme = useTheme()
  const [visible, setVisible] = useState<boolean>(false)

  const [snackbarData, setSnackbarData] = useState<IAlertProps>({
    severity: 'error',
    title: 'Something went wrong',
    text: '',
  })

  /**
   * VRG-607 — every toast in this app passes through here, so this is where raw
   * engineering text gets stopped.
   *
   * Three call sites in TwoFactorDrawer do `err instanceof Error ? err.message : …`,
   * and the messages reaching them come from `useAuthFactors`'s
   * `extractApiErrorMessage`, which returns the server's `data.message` verbatim
   * and filters exactly one string ('Validation errors'). So NestJS's own
   * defaults — "Unauthorized", "Forbidden resource", "Internal server error",
   * "Cannot POST /auth/customer/2fa/totp/enroll" — went straight to a shopper.
   *
   * Fixing those three call sites would fix those three call sites. This is a
   * consumer app; the next one added has the same odds. Filtering here cannot be
   * forgotten by a new caller, which is the whole point (working-conventions §42:
   * an opt-in convention 35 of 37 call sites skipped).
   *
   * The severity is NOT reinterpreted — only the words change. A failure still
   * reads as a failure.
   */
  const openSnackbar = ({ severity, title, text, action }: IAlertProps) => {
    // The title carries the headline ("Payment failed"), so a technical title is
    // replaced outright rather than given a generic body it doesn't match.
    const safeTitle =
      title && isTechnicalMessage(title)
        ? severity === 'error'
          ? 'Something went wrong'
          : undefined
        : title

    // `text` is the detail line and is where raw error strings actually land.
    // Passing `undefined` as the fallback keeps a toast that had no body from
    // gaining a generic one it never asked for.
    const safeText = text
      ? userFacingMessage(text, severity === 'error' ? GENERIC_ERROR_TEXT : '')
      : text

    setSnackbarData({ severity, title: safeTitle, text: safeText || undefined, action })
    setVisible(true)
  }

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setVisible(false)
  }

  return (
    <SnackbarContext.Provider
      value={{
        openSnackbar,
      }}
    >
      {children}

      <Snackbar
        open={visible}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 24, sm: 40 } }}
      >
        {/*
          The Box exists for the TRANSITION, not for layout. MUI's Snackbar clones its
          child inside a Grow, which drives the animation by passing `style` (and a ref)
          down. `@repo/ui`'s Alert destructures a fixed prop list and does not spread the
          rest, so a `style` handed to it is dropped on the floor — the toast would not
          animate and, worse, would not pick up the `visibility: hidden` Grow applies on
          exit. Wrapping is also what apps/platform's SnackbarProvider does (its own
          Fade wraps a Box for the same reason).
        */}
        <Box>
          {/*
            VERA-315: the SHARED @repo/ui Alert in its `neutral` variant — byte-for-byte
            the same component and variant apps/platform's toasts use (see
            apps/platform/src/contexts/SnackbarContext/SnackbarProvider.tsx `ToastRow`).
            That gets this app platform's exact toast treatment for free: a light card
            rather than a colour-flooded bar, the severity carried as a 1px hue border
            over a 6% tint of the same hue, a severity-coloured glyph, the lifted shadow,
            the 8px radius, and the shared entrance/dismiss motion.

            It replaces a hand-rolled MUI Alert that painted the surface
            `theme.palette.text.primary` and the text `theme.palette.background.default`
            — an inversion meant to guarantee contrast under any tenant. Two problems.
            (1) This provider mounts in index.tsx UNDER the static `appTheme`, ABOVE the
            per-tenant ThemeProvider that App.tsx creates from the Studio styles, so
            `useTheme()` here never saw a tenant at all: it read appTheme, whose
            `text.primary` is MUI's default `rgba(0,0,0,0.87)`. The inversion was
            therefore not adaptive, it was a black pill on every brand. (2) Even had it
            worked, a bar that flips light/dark per tenant is a second visual language
            for the same message class the platform already renders one way.
          */}
          <Alert
            severity={snackbarData.severity}
            variant="outlined"
            neutral
            title={snackbarData.title}
            description={snackbarData.text}
            button={snackbarData.action ? <div onClick={handleClose}>{snackbarData.action}</div> : undefined}
            sx={{
              // Full width of the Snackbar root, which MUI already insets 8px from both
              // edges at xs. Platform's `minWidth: 480` is deliberately NOT copied — this
              // app is verified at 375x812 (working-conventions §37) and a 480px floor
              // overflows the viewport it is anchored in.
              width: '100%',
              maxWidth: 'min(600px, 92vw)',

              // ---- The two tokens this app overrides, and why they are tokens ----
              //
              // `background.paper`, not a literal '#fff' and emphatically not
              // `common.black`. It resolves white today, which is the white toast asked
              // for, and it MOVES if a theme layer ever resolves a paper colour —
              // whereas a hex is frozen the moment it is typed.
              //
              // It is also the one place this deviates from platform's neutral tokens,
              // and the deviation is what MATCHES platform's intent rather than its
              // value. `@repo/ui` picks '#f6f6f6' for the toast surface with the reason
              // stated in Alert.tsx: the platform page is pure white, so an identical
              // surface had no separation. This app's page is NOT white — it is
              // `bg_color || '#F7F7F8'` (settings/mui/palette.ts), a soft grey or the
              // brand's own colour. Against '#F7F7F8' a '#f6f6f6' toast is a 1-value
              // difference, i.e. exactly the invisible card that comment exists to
              // prevent. Pure white is what separates here.
              backgroundColor: theme.palette.background.paper,

              // Paired with the surface above BY CONSTRUCTION — MUI defines
              // text.primary and background.paper for the same mode, so contrast cannot
              // drift apart as either moves.
              //
              // Deliberately NOT the tenant's Studio `text_color`. That token is chosen
              // by the brand against their `bg_color`, so a brand running white-on-black
              // would paint white text onto this white card. A tenant colour is only
              // safe on a surface the tenant also chose; the card is ours, so the text
              // on it is ours. The tenant's colour still reaches this toast where it is
              // safe — the typeface, and the page it floats over.
              color: theme.palette.text.primary,
              '& .MuiAlert-message': {
                minWidth: 0,
                overflowWrap: 'anywhere',
                color: theme.palette.text.primary,
              },
              // 15px/400 rather than the Alert's 14px/500: toast copy is a sentence, not
              // a heading. Matches platform's ToastRow override exactly.
              '& .MuiAlertTitle-root': {
                fontWeight: 400,
                fontSize: 15,
                overflowWrap: 'anywhere',
                color: theme.palette.text.primary,
              },
              '& .MuiTypography-body3': {
                fontSize: 13,
                overflowWrap: 'anywhere',
                color: alpha(theme.palette.text.primary, 0.75),
              },
            }}
          />
          {/*
            The severity BORDER, TINT and GLYPH are left exactly as `neutral` produces
            them, including the semantic green check on success. That is a deliberate
            platform decision, not an oversight to theme away: `@repo/ui` Alert's
            getNeutralTokens documents it — severity is the fastest pre-attentive channel
            for something this transient, and inline banners already use border+tint to
            mean valence, so a toast in the brand's accent would teach a second visual
            language for one meaning. Painting a failure toast in the brand's colour also
            makes the brand the thing that reads as wrong. `info` stays untinted, which is
            what keeps the other three legible as signals.
          */}
        </Box>
      </Snackbar>
    </SnackbarContext.Provider>
  )
}
